import { useState, useEffect, useMemo, useCallback } from 'react';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../../context/AuthContext';
import { validateCustomer } from '../../../utils/validationUtils';
import { normalizeTr } from '../../../utils/textUtils';
import { normalizeLoyalty } from '../../../domain/loyaltyCalculations';
// loyalty özelliklerine barrel üzerinden erişilir — direkt feature→feature import yasak
import {
  backfillCustomerPoints,
  shouldCheckExpiry,
  checkAndExpirePoints,
} from '../../loyalty';
import { AuditLogger } from '../../../utils/auditLogger';
import {
  fetchActiveCustomers,
  fetchActiveEmployees,
  addCustomer,
  updateCustomer,
  softDeleteCustomer,
} from '../services/customerService';
import { db } from '../../../firebase';
import useCustomersStore from '../store/customersStore';

export function useCustomers() {
  const { user } = useAuth();
  const { t } = useTranslation('customers');

  const [customers, setCustomers] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [saving, setSaving] = useState(false);

  const searchTerm = useCustomersStore((s) => s.searchTerm);
  const closeModal = useCustomersStore((s) => s.closeModal);
  const formData = useCustomersStore((s) => s.formData);
  const isEditing = useCustomersStore((s) => s.isEditing);
  const selectedCustomerId = useCustomersStore((s) => s.selectedCustomerId);
  const setBackfillLoading = useCustomersStore((s) => s.setBackfillLoading);
  const backfillLoading = useCustomersStore((s) => s.backfillLoading);

  // ── Fetch ─────────────────────────────────────────────────────────────────────
  const refresh = useCallback(async () => {
    if (!user?.businessId) return;
    try {
      const [c, e] = await Promise.all([
        fetchActiveCustomers(user.businessId),
        fetchActiveEmployees(user.businessId),
      ]);
      setCustomers(c);
      setEmployees(e);
    } catch (err) {
      console.error('[useCustomers] fetch error:', err);
    }
  }, [user?.businessId]);

  useEffect(() => {
    let cancelled = false;
    refresh().then(() => {}).catch(() => {});
    // Not strictly needed for getDocs but good practice
    return () => { cancelled = true; };
  }, [refresh]);

  // ── Filtered list (client-side, useMemo) ──────────────────────────────────────
  const filteredCustomers = useMemo(() => {
    const term = normalizeTr(searchTerm);
    if (!term) return customers;

    const terms = term.split(/\s+/);

    return customers.filter((c) => {
      const name    = normalizeTr(c.name);
      const surname = normalizeTr(c.surname);
      const full = `${name} ${surname}`;
      const rev  = `${surname} ${name}`;
      const phone = normalizeTr(`${c.phone?.countryCode || ''}${c.phone?.number || ''}`).replace(/\s+/g, '');
      const email = normalizeTr(c.emailAddress);

      // Her kelime en az bir alanda eşleşmeli (AND) — böylece "gökhan 532" gibi
      // ad + telefon birleşik aramalar da çalışır.
      const matches = (w) =>
        full.includes(w) || rev.includes(w) || phone.includes(w) || email.includes(w);

      return terms.every(matches);
    });
  }, [customers, searchTerm]);

  // ── Save (add / update) ───────────────────────────────────────────────────────
  const save = useCallback(async () => {
    if (!user?.businessId) return;
    setSaving(true);
    try {
      const validation = await validateCustomer(formData, db, selectedCustomerId, user.businessId);
      if (!validation.isValid) {
        toast.error(Object.values(validation.errors).join('\n'));
        return;
      }

      if (isEditing) {
        await updateCustomer(selectedCustomerId, formData, user.businessId);
        toast.success(t('alert.customerUpdated'));
      } else {
        await addCustomer(formData, user.businessId);
        toast.success(t('alert.customerAdded'));
      }

      closeModal();
      await refresh();
    } catch (err) {
      console.error('[useCustomers] save error:', err);
      toast.error(`${t('alert.error')}: ${err.message}`);
    } finally {
      setSaving(false);
    }
  }, [user?.businessId, formData, isEditing, selectedCustomerId, closeModal, refresh, t]);

  // ── Soft delete ───────────────────────────────────────────────────────────────
  const remove = useCallback(async (customerId) => {
    try {
      await softDeleteCustomer(customerId);
      await refresh();
    } catch (err) {
      console.error('[useCustomers] delete error:', err);
      toast.error(t('alert.error'));
    }
  }, [refresh, t]);

  // ── Lazy expiry check (non-blocking) ─────────────────────────────────────────
  const triggerExpiryCheck = useCallback((customer) => {
    if (!customer || !user) return;
    const loyalty = normalizeLoyalty(customer.loyalty);
    if (!shouldCheckExpiry(loyalty)) return;

    checkAndExpirePoints(customer.id, user.businessId)
      .then(async (result) => {
        if (result.expiredPoints > 0) {
          console.info(`[Expiry] ${customer.id}: ${result.expiredPoints} puan süresi doldu`);
          try {
            await AuditLogger.log(user.uid, 'expire', 'loyalty', customer.id, {
              expiredPoints: result.expiredPoints,
              txsCreated: result.txsCreated,
              newTier: result.newTier,
              trigger: 'customer-view',
            });
          } catch { /* audit log kritik değil */ }
          refresh();
        }
      })
      .catch((err) => console.warn('[useCustomers] expiry check failed:', err));
  }, [user, refresh]);

  // ── Backfill ──────────────────────────────────────────────────────────────────
  const backfill = useCallback(async (customer) => {
    if (!customer || !user) return;
    const loyalty = normalizeLoyalty(customer.loyalty);
    if (loyalty.backfillCompletedAt) {
      toast.warning(t('alert.backfillAlreadyDone'));
      return;
    }

    if (!window.confirm(t('alert.backfillConfirm', { name: `${customer.name} ${customer.surname || ''}` }))) {
      return;
    }

    setBackfillLoading(customer.id);
    try {
      const result = await backfillCustomerPoints(customer.id, user.uid, user.businessId);

      try {
        await AuditLogger.log(user.uid, 'backfill', 'loyalty', customer.id, {
          customerId: customer.id,
          billCount: result.billCount,
          processedCount: result.processedCount,
          totalEarned: result.totalEarned,
          finalCurrentPoints: result.finalCurrentPoints,
          finalTier: result.finalTier,
        });
      } catch { /* audit log kritik değil */ }

      toast.success(
        t('alert.backfillSuccess') + '\n' +
        t('alert.backfillCounts', { billCount: result.billCount, processedCount: result.processedCount, skippedCount: result.skippedCount }) + '\n' +
        t('alert.backfillEarned', { points: result.totalEarned })
      );
      await refresh();
    } catch (err) {
      console.error('[useCustomers] backfill error:', err);
      if (err.code === 'already-backfilled') toast.warning(t('alert.backfillAlreadyDone'));
      else if (err.code === 'too-many-bills') toast.error(err.message);
      else if (err.code === 'customer-not-found') toast.error(t('alert.customerNotFound'));
      else toast.error(`${t('alert.backfillFailed')}: ${err.message}`);
    } finally {
      setBackfillLoading(null);
    }
  }, [user, refresh, t, setBackfillLoading]);

  return {
    customers,
    filteredCustomers,
    employees,
    saving,
    refresh,
    save,
    remove,
    backfill,
    backfillLoading,
    triggerExpiryCheck,
  };
}
